import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { CloudDownload } from "@mui/icons-material";
import { renderAsync as renderDocxAsync } from "docx-preview";
import { makeStyles } from "@/compat/muiStyles";
import CardBase from "../cards/CardBase";

const PDF_MIME = "application/pdf";
const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const useStyles = makeStyles(() => ({
    iframe: {
        width: "100%",
        height: "100vh",
        border: "none",
    },
    docx: {
        maxHeight: "100vh",
        overflow: "auto",
        backgroundColor: "#fff",
    },
    centered: {
        display: "flex",
        justifyContent: "center",
        padding: 32,
    },
    fallback: {
        padding: 24,
        textAlign: "center",
    },
}));

// Decode a base64 payload into raw bytes for docx-preview / blob downloads.
function base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

// Renders a .docx inline using docx-preview (the CaseNest FileViewer strategy).
function DocxPreview({ fileContents }: { fileContents: string }) {
    const classes = useStyles();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const styleRef = useRef<HTMLDivElement | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const bytes = base64ToBytes(fileContents);
                if (cancelled || !containerRef.current) return;
                containerRef.current.replaceChildren();
                styleRef.current?.replaceChildren();
                await renderDocxAsync(bytes, containerRef.current, styleRef.current ?? undefined, {
                    className: "docx",
                    inWrapper: true,
                    ignoreWidth: false,
                    ignoreHeight: false,
                    ignoreFonts: false,
                    breakPages: true,
                    experimental: true,
                    useBase64URL: true,
                });
            } catch (e) {
                console.error(e);
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [fileContents]);

    if (failed)
        return (
            <Typography variant="body2" className={classes.fallback}>
                This document couldn't be previewed. Use the download button to open it.
            </Typography>
        );

    return (
        <Box className={classes.docx}>
            <div ref={styleRef} />
            {loading && (
                <Box className={classes.centered}>
                    <CircularProgress size={28} />
                </Box>
            )}
            <div ref={containerRef} />
        </Box>
    );
}

export default function DocumentViewer({
    fileType,
    fileContents,
    fileName,
}: {
    fileType?: string;
    fileContents?: string;
    fileName?: string;
}) {
    const classes = useStyles();

    const downloadUrl = useMemo(() => {
        if (!fileContents) return undefined;
        try {
            const bytes = base64ToBytes(fileContents);
            return URL.createObjectURL(new Blob([bytes.buffer as ArrayBuffer], { type: fileType || "application/octet-stream" }));
        } catch (e) {
            console.error(e);
            return undefined;
        }
    }, [fileContents, fileType]);

    useEffect(
        () => () => {
            if (downloadUrl) URL.revokeObjectURL(downloadUrl);
        },
        [downloadUrl]
    );

    const download = () => {
        if (!downloadUrl) return;
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName || "document";
        link.click();
    };

    // Document not delivered yet.
    if (!fileContents || !fileType)
        return (
            <CardBase>
                <Box className={classes.centered}>
                    <CircularProgress size={28} />
                </Box>
            </CardBase>
        );

    if (fileType === PDF_MIME)
        return (
            <CardBase>
                <iframe className={classes.iframe} title="Document Viewer" src={"data:" + PDF_MIME + ";base64," + fileContents} />
            </CardBase>
        );

    if (fileType === DOCX_MIME)
        return (
            <CardBase>
                <DocxPreview fileContents={fileContents} />
            </CardBase>
        );

    return (
        <CardBase>
            <Box className={classes.fallback}>
                <Typography variant="body2" gutterBottom>
                    Preview isn't available for this file type. Download it to review.
                </Typography>
                <Button variant="contained" color="primary" startIcon={<CloudDownload />} onClick={download}>
                    Download document
                </Button>
            </Box>
        </CardBase>
    );
}
